// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import { configApi } from '@/api/config';
import { PromptsList } from './PromptsList';
import { extractPromptName, extractAnalysisType } from '@/utils/prompts';
import { FileText, FolderOpen, AlertCircle, CheckCircle, Save, GitCompare, Layers } from 'lucide-react';

interface PromptTemplateConfig {
  promptType: 'default' | 'workspace';
  promptName: string | null;
  promptAnalysisType: string;
  templateType: 'default' | 'workspace';
  templateName: string | null;
}

export function MemberDefaultsConfigurationView() {
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();
  const queryClient = useQueryClient();

  // Single document config state
  const [singleDocConfig, setSingleDocConfig] = useState<PromptTemplateConfig>({
    promptType: 'default',
    promptName: null,
    promptAnalysisType: 'analyzer',
    templateType: 'default',
    templateName: null,
  });

  // Bulk document config state
  const [bulkDocConfig, setBulkDocConfig] = useState<PromptTemplateConfig>({
    promptType: 'default',
    promptName: null,
    promptAnalysisType: 'bulk_analyzer',
    templateType: 'default',
    templateName: null,
  });

  // Comparison config state (for 2 documents)
  const [comparisonConfig, setComparisonConfig] = useState<PromptTemplateConfig>({
    promptType: 'default',
    promptName: null,
    promptAnalysisType: 'comparator',
    templateType: 'default',
    templateName: null,
  });

  // Bulk comparison config state (for 3+ documents)
  const [bulkComparisonConfig, setBulkComparisonConfig] = useState<PromptTemplateConfig>({
    promptType: 'default',
    promptName: null,
    promptAnalysisType: 'bulk_comparator',
    templateType: 'default',
    templateName: null,
  });

  // Grade data comparison config state
  const [gradeDataComparisonConfig, setGradeDataComparisonConfig] = useState<PromptTemplateConfig>({
    promptType: 'default',
    promptName: null,
    promptAnalysisType: 'comparator',
    templateType: 'default',
    templateName: null,
  });

  // Other document type analysis config state
  const [otherDocConfig, setOtherDocConfig] = useState<PromptTemplateConfig>({
    promptType: 'default',
    promptName: null,
    promptAnalysisType: 'other',
    templateType: 'default',
    templateName: null,
  });

  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch workspace config
  const { data: workspaceConfig, isLoading: configLoading } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected');
      }
      return configApi.getWorkspaceConfig(selectedOrganizationId, selectedWorkspaceId);
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  });

  // Load config into state when fetched
  useEffect(() => {
    if (workspaceConfig) {
      setSingleDocConfig({
        promptType: (workspaceConfig.member_single_doc_prompt_type as 'default' | 'workspace') || 'default',
        promptName: workspaceConfig.member_single_doc_prompt_name || null,
        promptAnalysisType: workspaceConfig.member_single_doc_prompt_analysis_type || 'analyzer',
        templateType: (workspaceConfig.member_single_doc_template_type as 'default' | 'workspace') || 'default',
        templateName: workspaceConfig.member_single_doc_template_name || null,
      });

      setBulkDocConfig({
        promptType: (workspaceConfig.member_bulk_doc_prompt_type as 'default' | 'workspace') || 'default',
        promptName: workspaceConfig.member_bulk_doc_prompt_name || null,
        promptAnalysisType: workspaceConfig.member_bulk_doc_prompt_analysis_type || 'bulk_analyzer',
        templateType: (workspaceConfig.member_bulk_doc_template_type as 'default' | 'workspace') || 'default',
        templateName: workspaceConfig.member_bulk_doc_template_name || null,
      });

      setComparisonConfig({
        promptType: (workspaceConfig.member_comparison_prompt_type as 'default' | 'workspace') || 'default',
        promptName: workspaceConfig.member_comparison_prompt_name || null,
        promptAnalysisType: workspaceConfig.member_comparison_prompt_analysis_type || 'comparator',
        templateType: 'default',
        templateName: null,
      });

      setBulkComparisonConfig({
        promptType: (workspaceConfig.member_bulk_comparison_prompt_type as 'default' | 'workspace') || 'default',
        promptName: workspaceConfig.member_bulk_comparison_prompt_name || null,
        promptAnalysisType: workspaceConfig.member_bulk_comparison_prompt_analysis_type || 'bulk_comparator',
        templateType: 'default',
        templateName: null,
      });

      setGradeDataComparisonConfig({
        promptType: (workspaceConfig.member_grade_data_comparison_prompt_type as 'default' | 'workspace') || 'default',
        promptName: workspaceConfig.member_grade_data_comparison_prompt_name || null,
        promptAnalysisType: workspaceConfig.member_grade_data_comparison_prompt_analysis_type || 'comparator',
        templateType: 'default',
        templateName: null,
      });

      setOtherDocConfig({
        promptType: (workspaceConfig.member_other_doc_prompt_type as 'default' | 'workspace') || 'default',
        promptName: workspaceConfig.member_other_doc_prompt_name || null,
        promptAnalysisType: workspaceConfig.member_other_doc_prompt_analysis_type || 'other',
        templateType: 'default',
        templateName: null,
      });
    }
  }, [workspaceConfig]);

  // Fetch available prompts for single doc (analyzer)
  const { data: singleDocPrompts = [], isLoading: singlePromptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', singleDocConfig.promptType, selectedOrganizationId, selectedWorkspaceId, 'analyzer'],
    queryFn: async () => {
      const allPrompts = await configApi.getPrompts(
        singleDocConfig.promptType,
        singleDocConfig.promptType === 'workspace' ? selectedOrganizationId : undefined,
        singleDocConfig.promptType === 'workspace' ? selectedWorkspaceId : undefined
      );
      return allPrompts.filter(prompt => prompt.startsWith('analyzer/'));
    },
    enabled: Boolean(
      singleDocConfig.promptType === 'default' ||
      (singleDocConfig.promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available prompts for bulk doc (bulk_analyzer)
  const { data: bulkDocPrompts = [], isLoading: bulkPromptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', bulkDocConfig.promptType, selectedOrganizationId, selectedWorkspaceId, 'bulk_analyzer'],
    queryFn: async () => {
      const allPrompts = await configApi.getPrompts(
        bulkDocConfig.promptType,
        bulkDocConfig.promptType === 'workspace' ? selectedOrganizationId : undefined,
        bulkDocConfig.promptType === 'workspace' ? selectedWorkspaceId : undefined
      );
      return allPrompts.filter(prompt => prompt.startsWith('bulk analyzer/'));
    },
    enabled: Boolean(
      bulkDocConfig.promptType === 'default' ||
      (bulkDocConfig.promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available templates for single doc
  const { data: singleDocTemplates = [], isLoading: singleTemplatesLoading } = useQuery<string[]>({
    queryKey: ['templates', singleDocConfig.templateType, selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      return configApi.getTemplates(
        singleDocConfig.templateType,
        singleDocConfig.templateType === 'workspace' ? selectedOrganizationId : undefined,
        singleDocConfig.templateType === 'workspace' ? selectedWorkspaceId : undefined
      );
    },
    enabled: Boolean(
      singleDocConfig.templateType === 'default' ||
      (singleDocConfig.templateType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available templates for bulk doc
  const { data: bulkDocTemplates = [], isLoading: bulkTemplatesLoading } = useQuery<string[]>({
    queryKey: ['templates', bulkDocConfig.templateType, selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      return configApi.getTemplates(
        bulkDocConfig.templateType,
        bulkDocConfig.templateType === 'workspace' ? selectedOrganizationId : undefined,
        bulkDocConfig.templateType === 'workspace' ? selectedWorkspaceId : undefined
      );
    },
    enabled: Boolean(
      bulkDocConfig.templateType === 'default' ||
      (bulkDocConfig.templateType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available prompts for comparison (comparator)
  const { data: comparisonPrompts = [], isLoading: comparisonPromptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', comparisonConfig.promptType, selectedOrganizationId, selectedWorkspaceId, 'comparator'],
    queryFn: async () => {
      const allPrompts = await configApi.getPrompts(
        comparisonConfig.promptType,
        comparisonConfig.promptType === 'workspace' ? selectedOrganizationId : undefined,
        comparisonConfig.promptType === 'workspace' ? selectedWorkspaceId : undefined
      );
      return allPrompts.filter(prompt => prompt.startsWith('comparator/'));
    },
    enabled: Boolean(
      comparisonConfig.promptType === 'default' ||
      (comparisonConfig.promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available prompts for bulk comparison (bulk_comparator)
  const { data: bulkComparisonPrompts = [], isLoading: bulkComparisonPromptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', bulkComparisonConfig.promptType, selectedOrganizationId, selectedWorkspaceId, 'bulk_comparator'],
    queryFn: async () => {
      const allPrompts = await configApi.getPrompts(
        bulkComparisonConfig.promptType,
        bulkComparisonConfig.promptType === 'workspace' ? selectedOrganizationId : undefined,
        bulkComparisonConfig.promptType === 'workspace' ? selectedWorkspaceId : undefined
      );
      return allPrompts.filter(prompt => prompt.startsWith('bulk comparator/'));
    },
    enabled: Boolean(
      bulkComparisonConfig.promptType === 'default' ||
      (bulkComparisonConfig.promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available prompts for other document type (other/)
  const { data: otherDocPrompts = [], isLoading: otherDocPromptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', otherDocConfig.promptType, selectedOrganizationId, selectedWorkspaceId, 'other'],
    queryFn: async () => {
      const allPrompts = await configApi.getPrompts(
        otherDocConfig.promptType,
        otherDocConfig.promptType === 'workspace' ? selectedOrganizationId : undefined,
        otherDocConfig.promptType === 'workspace' ? selectedWorkspaceId : undefined
      );
      return allPrompts.filter(prompt => prompt.startsWith('other/'));
    },
    enabled: Boolean(
      otherDocConfig.promptType === 'default' ||
      (otherDocConfig.promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Fetch available prompts for grade data comparison
  const { data: gradeDataComparisonPrompts = [], isLoading: gradeDataComparisonPromptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', gradeDataComparisonConfig.promptType, selectedOrganizationId, selectedWorkspaceId, 'grade_data_comparator'],
    queryFn: async () => {
      const allPrompts = await configApi.getPrompts(
        gradeDataComparisonConfig.promptType,
        gradeDataComparisonConfig.promptType === 'workspace' ? selectedOrganizationId : undefined,
        gradeDataComparisonConfig.promptType === 'workspace' ? selectedWorkspaceId : undefined
      );
      // Filter for comparator prompts (grade data uses regular comparator prompts)
      return allPrompts.filter(prompt => prompt.startsWith('comparator/'));
    },
    enabled: Boolean(
      gradeDataComparisonConfig.promptType === 'default' ||
      (gradeDataComparisonConfig.promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected');
      }

      const updateData = {
        member_single_doc_prompt_type: singleDocConfig.promptName ? singleDocConfig.promptType : undefined,
        member_single_doc_prompt_name: singleDocConfig.promptName || undefined,
        member_single_doc_prompt_analysis_type: singleDocConfig.promptName ? singleDocConfig.promptAnalysisType : undefined,
        member_single_doc_template_type: singleDocConfig.templateName ? singleDocConfig.templateType : undefined,
        member_single_doc_template_name: singleDocConfig.templateName || undefined,
        member_bulk_doc_prompt_type: bulkDocConfig.promptName ? bulkDocConfig.promptType : undefined,
        member_bulk_doc_prompt_name: bulkDocConfig.promptName || undefined,
        member_bulk_doc_prompt_analysis_type: bulkDocConfig.promptName ? bulkDocConfig.promptAnalysisType : undefined,
        member_bulk_doc_template_type: bulkDocConfig.templateName ? bulkDocConfig.templateType : undefined,
        member_bulk_doc_template_name: bulkDocConfig.templateName || undefined,
        member_comparison_prompt_type: comparisonConfig.promptName ? comparisonConfig.promptType : undefined,
        member_comparison_prompt_name: comparisonConfig.promptName || undefined,
        member_comparison_prompt_analysis_type: comparisonConfig.promptName ? comparisonConfig.promptAnalysisType : undefined,
        member_bulk_comparison_prompt_type: bulkComparisonConfig.promptName ? bulkComparisonConfig.promptType : undefined,
        member_bulk_comparison_prompt_name: bulkComparisonConfig.promptName || undefined,
        member_bulk_comparison_prompt_analysis_type: bulkComparisonConfig.promptName ? bulkComparisonConfig.promptAnalysisType : undefined,
        member_grade_data_comparison_prompt_type: gradeDataComparisonConfig.promptName ? gradeDataComparisonConfig.promptType : undefined,
        member_grade_data_comparison_prompt_name: gradeDataComparisonConfig.promptName || undefined,
        member_grade_data_comparison_prompt_analysis_type: gradeDataComparisonConfig.promptName ? gradeDataComparisonConfig.promptAnalysisType : undefined,
        member_other_doc_prompt_type: otherDocConfig.promptName ? otherDocConfig.promptType : undefined,
        member_other_doc_prompt_name: otherDocConfig.promptName || undefined,
        member_other_doc_prompt_analysis_type: otherDocConfig.promptName ? otherDocConfig.promptAnalysisType : undefined,
      };

      console.log('Saving member defaults config:', {
        comparisonConfig,
        bulkComparisonConfig,
        gradeDataComparisonConfig,
        updateData
      });

      const result = await configApi.updateWorkspaceConfig(selectedOrganizationId, selectedWorkspaceId, updateData);
      console.log('Save result:', result);
      return result;
    },
    onSuccess: () => {
      console.log('Save successful, invalidating cache');
      queryClient.invalidateQueries({ queryKey: ['workspaceConfig'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error) => {
      console.error('Save failed:', error);
    },
  });

  const handleSave = () => {
    console.log('handleSave clicked!', {
      singleDocConfig,
      bulkDocConfig,
      comparisonConfig,
      bulkComparisonConfig,
      gradeDataComparisonConfig
    });
    saveMutation.mutate();
  };

  const isSingleDocConfigured = Boolean(singleDocConfig.promptName && singleDocConfig.templateName);
  const isBulkDocConfigured = Boolean(bulkDocConfig.promptName && bulkDocConfig.templateName);
  const isComparisonConfigured = Boolean(comparisonConfig.promptName);
  const isBulkComparisonConfigured = Boolean(bulkComparisonConfig.promptName);
  const isGradeDataComparisonConfigured = Boolean(gradeDataComparisonConfig.promptName);
  const isOtherDocConfigured = Boolean(otherDocConfig.promptName);

  if (configLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Member Analysis Defaults</h3>
            <p className="text-sm text-blue-800">
              Configure default prompts and templates that member users will automatically use when performing analysis.
              Members will not be able to select their own prompts/templates.
            </p>
          </div>
        </div>
      </div>

      {/* Single Document Analysis Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Single Document Analysis</h3>
        </div>

        <div className="space-y-4">
          {/* Prompt Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Prompt Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setSingleDocConfig(prev => ({ ...prev, promptType: 'default', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    singleDocConfig.promptType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setSingleDocConfig(prev => ({ ...prev, promptType: 'workspace', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    singleDocConfig.promptType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Prompt</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {singlePromptsLoading ? (
                  <p className="text-sm text-gray-500">Loading prompts...</p>
                ) : singleDocPrompts.length > 0 ? (
                  <PromptsList
                    level={singleDocConfig.promptType}
                    selectedItem={singleDocConfig.promptName ? { name: singleDocConfig.promptName, analysisType: singleDocConfig.promptAnalysisType } : null}
                    onSelectItem={(promptPath) => {
                      const promptName = extractPromptName(promptPath);
                      const analysisType = extractAnalysisType(promptPath);
                      setSingleDocConfig(prev => ({ ...prev, promptName, promptAnalysisType: analysisType }));
                    }}
                    prompts={singleDocPrompts}
                    isLoading={singlePromptsLoading}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No analyzer prompts available for {singleDocConfig.promptType} configuration</p>
                )}
              </div>
            </div>
          </div>

          {/* Template Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Template Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setSingleDocConfig(prev => ({ ...prev, templateType: 'default', templateName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    singleDocConfig.templateType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setSingleDocConfig(prev => ({ ...prev, templateType: 'workspace', templateName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    singleDocConfig.templateType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Template</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {singleTemplatesLoading ? (
                  <p className="text-sm text-gray-500">Loading templates...</p>
                ) : singleDocTemplates.length > 0 ? (
                  <div className="space-y-2">
                    {singleDocTemplates.map((template) => (
                      <button
                        key={template}
                        onClick={() => setSingleDocConfig(prev => ({ ...prev, templateName: template }))}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors border ${
                          singleDocConfig.templateName === template
                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                            : 'bg-white hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="font-medium">{template}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No templates available for {singleDocConfig.templateType} configuration</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Document Analysis Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <FolderOpen className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Bulk/Multi Document Analysis</h3>
        </div>

        <div className="space-y-4">
          {/* Prompt Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Prompt Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setBulkDocConfig(prev => ({ ...prev, promptType: 'default', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkDocConfig.promptType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setBulkDocConfig(prev => ({ ...prev, promptType: 'workspace', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkDocConfig.promptType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Prompt</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {bulkPromptsLoading ? (
                  <p className="text-sm text-gray-500">Loading prompts...</p>
                ) : bulkDocPrompts.length > 0 ? (
                  <PromptsList
                    level={bulkDocConfig.promptType}
                    selectedItem={bulkDocConfig.promptName ? { name: bulkDocConfig.promptName, analysisType: bulkDocConfig.promptAnalysisType } : null}
                    onSelectItem={(promptPath) => {
                      const promptName = extractPromptName(promptPath);
                      const analysisType = extractAnalysisType(promptPath);
                      setBulkDocConfig(prev => ({ ...prev, promptName, promptAnalysisType: analysisType }));
                    }}
                    prompts={bulkDocPrompts}
                    isLoading={bulkPromptsLoading}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No bulk analyzer prompts available for {bulkDocConfig.promptType} configuration</p>
                )}
              </div>
            </div>
          </div>

          {/* Template Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Template Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setBulkDocConfig(prev => ({ ...prev, templateType: 'default', templateName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkDocConfig.templateType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setBulkDocConfig(prev => ({ ...prev, templateType: 'workspace', templateName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkDocConfig.templateType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Template</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {bulkTemplatesLoading ? (
                  <p className="text-sm text-gray-500">Loading templates...</p>
                ) : bulkDocTemplates.length > 0 ? (
                  <div className="space-y-2">
                    {bulkDocTemplates.map((template) => (
                      <button
                        key={template}
                        onClick={() => setBulkDocConfig(prev => ({ ...prev, templateName: template }))}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors border ${
                          bulkDocConfig.templateName === template
                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                            : 'bg-white hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="font-medium">{template}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No templates available for {bulkDocConfig.templateType} configuration</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Configuration (2 documents) */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <GitCompare className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Comparison (2 Documents)</h3>
        </div>

        <div className="space-y-4">
          {/* Prompt Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Prompt Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setComparisonConfig(prev => ({ ...prev, promptType: 'default', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    comparisonConfig.promptType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setComparisonConfig(prev => ({ ...prev, promptType: 'workspace', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    comparisonConfig.promptType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Prompt</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {comparisonPromptsLoading ? (
                  <p className="text-sm text-gray-500">Loading prompts...</p>
                ) : comparisonPrompts.length > 0 ? (
                  <PromptsList
                    level={comparisonConfig.promptType}
                    selectedItem={comparisonConfig.promptName ? { name: comparisonConfig.promptName, analysisType: comparisonConfig.promptAnalysisType } : null}
                    onSelectItem={(promptPath) => {
                      const promptName = extractPromptName(promptPath);
                      const analysisType = extractAnalysisType(promptPath);
                      setComparisonConfig(prev => ({ ...prev, promptName, promptAnalysisType: analysisType }));
                    }}
                    prompts={comparisonPrompts}
                    isLoading={comparisonPromptsLoading}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No comparator prompts available for {comparisonConfig.promptType} configuration</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Comparison Configuration (3+ documents) */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Bulk Comparison (3+ Documents)</h3>
        </div>

        <div className="space-y-4">
          {/* Prompt Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Prompt Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setBulkComparisonConfig(prev => ({ ...prev, promptType: 'default', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkComparisonConfig.promptType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setBulkComparisonConfig(prev => ({ ...prev, promptType: 'workspace', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkComparisonConfig.promptType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Prompt</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {bulkComparisonPromptsLoading ? (
                  <p className="text-sm text-gray-500">Loading prompts...</p>
                ) : bulkComparisonPrompts.length > 0 ? (
                  <PromptsList
                    level={bulkComparisonConfig.promptType}
                    selectedItem={bulkComparisonConfig.promptName ? { name: bulkComparisonConfig.promptName, analysisType: bulkComparisonConfig.promptAnalysisType } : null}
                    onSelectItem={(promptPath) => {
                      const promptName = extractPromptName(promptPath);
                      const analysisType = extractAnalysisType(promptPath);
                      setBulkComparisonConfig(prev => ({ ...prev, promptName, promptAnalysisType: analysisType }));
                    }}
                    prompts={bulkComparisonPrompts}
                    isLoading={bulkComparisonPromptsLoading}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No bulk comparator prompts available for {bulkComparisonConfig.promptType} configuration</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grade Data Comparison Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h3 className="text-lg font-bold text-gray-900">Grade Data Comparison</h3>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            This prompt will be automatically used when comparing documents that contain grade data. 
            It allows for specialized prompts tailored to grade data analysis.
          </p>
        </div>

        <div className="space-y-4">
          {/* Prompt Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Prompt Configuration</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setGradeDataComparisonConfig(prev => ({ ...prev, promptType: 'default', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    gradeDataComparisonConfig.promptType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setGradeDataComparisonConfig(prev => ({ ...prev, promptType: 'workspace', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    gradeDataComparisonConfig.promptType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Prompt</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {gradeDataComparisonPromptsLoading ? (
                  <p className="text-sm text-gray-500">Loading prompts...</p>
                ) : gradeDataComparisonPrompts.length > 0 ? (
                  <PromptsList
                    level={gradeDataComparisonConfig.promptType}
                    selectedItem={gradeDataComparisonConfig.promptName ? { name: gradeDataComparisonConfig.promptName, analysisType: gradeDataComparisonConfig.promptAnalysisType } : null}
                    onSelectItem={(promptPath) => {
                      const promptName = extractPromptName(promptPath);
                      const analysisType = extractAnalysisType(promptPath);
                      setGradeDataComparisonConfig(prev => ({ ...prev, promptName, promptAnalysisType: analysisType }));
                    }}
                    prompts={gradeDataComparisonPrompts}
                    isLoading={gradeDataComparisonPromptsLoading}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No comparator prompts available for {gradeDataComparisonConfig.promptType} configuration</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Other Document Analysis Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Other Document Analysis</h3>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            This prompt will be automatically used when analyzing documents of type "Other".
            No template is required — the output will be returned as formatted text.
          </p>
        </div>

        <div className="space-y-4">
          {/* Prompt Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Prompt Configuration</h4>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Type</label>
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setOtherDocConfig(prev => ({ ...prev, promptType: 'default', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    otherDocConfig.promptType === 'default'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Default
                </button>
                <button
                  onClick={() => setOtherDocConfig(prev => ({ ...prev, promptType: 'workspace', promptName: null }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    otherDocConfig.promptType === 'workspace'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Workspace
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Prompt</label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                {otherDocPromptsLoading ? (
                  <p className="text-sm text-gray-500">Loading prompts...</p>
                ) : otherDocPrompts.length > 0 ? (
                  <PromptsList
                    level={otherDocConfig.promptType}
                    selectedItem={otherDocConfig.promptName ? { name: otherDocConfig.promptName, analysisType: otherDocConfig.promptAnalysisType } : null}
                    onSelectItem={(promptPath) => {
                      const promptName = extractPromptName(promptPath);
                      const analysisType = extractAnalysisType(promptPath);
                      setOtherDocConfig(prev => ({ ...prev, promptName, promptAnalysisType: analysisType }));
                    }}
                    prompts={otherDocPrompts}
                    isLoading={otherDocPromptsLoading}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No other doc prompts available for {otherDocConfig.promptType} configuration</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Status */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Current Configuration Status
        </h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {isSingleDocConfigured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <span className="text-sm text-gray-700">
              Single Document Analysis: {isSingleDocConfigured ? '✓ Configured' : '⚠ Not configured (members cannot analyze single documents)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isBulkDocConfigured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <span className="text-sm text-gray-700">
              Bulk Document Analysis: {isBulkDocConfigured ? '✓ Configured' : '⚠ Not configured (members cannot analyze multiple documents)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isComparisonConfigured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <span className="text-sm text-gray-700">
              Comparison (2 docs): {isComparisonConfigured ? '✓ Configured' : '⚠ Not configured (members cannot compare 2 documents)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isBulkComparisonConfigured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <span className="text-sm text-gray-700">
              Bulk Comparison (3+ docs): {isBulkComparisonConfigured ? '✓ Configured' : '⚠ Not configured (members cannot compare 3+ documents)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isGradeDataComparisonConfigured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <span className="text-sm text-gray-700">
              Grade Data Comparison: {isGradeDataComparisonConfigured ? '✓ Configured' : '⚠ Not configured (members cannot compare grade data documents with specialized prompts)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isOtherDocConfigured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <span className="text-sm text-gray-700">
              Other Document Analysis: {isOtherDocConfigured ? '✓ Configured' : '⚠ Not configured (members cannot analyze "other" type documents)'}
            </span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={(e) => {
            console.log('Save button clicked!', e);
            handleSave();
          }}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-5 h-5" />
          {saveMutation.isPending ? 'Saving...' : 'Save Member Defaults'}
        </button>

        {saveSuccess && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Settings saved successfully!</span>
          </div>
        )}

        {saveMutation.isError && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Failed to save settings</span>
          </div>
        )}
      </div>
    </div>
  );
}
